import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories, resources, features, faqs, testimonials } from '@/mocks/data';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { homeSchema, faqSchema } from '@/mocks/schema';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const featuredResources = resources.filter((r) => r.featured).slice(0, 12);

  useEffect(() => {
    const scriptId = 'schema-home';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify(homeSchema);
    document.head.appendChild(script);

    const faqScriptId = 'schema-faq';
    const faqExisting = document.getElementById(faqScriptId);
    if (faqExisting) faqExisting.remove();

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = faqScriptId;
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
      const fs = document.getElementById(faqScriptId);
      if (fs) fs.remove();
    };
  }, []);

  return (
    <>
      <SEOHead title="首页" description="91.n 是您值得信赖的网络导航入口，提供快速、安全、便捷的访问体验。汇聚核心功能介绍、使用指南及常见问题解答。" />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20abstract%20technology%20background%20with%20warm%20gradient%20tones%20of%20orange%20and%20amber%2C%20soft%20geometric%20shapes%2C%20light%20and%20airy%20atmosphere%2C%20clean%20digital%20design%2C%20smooth%20curves%2C%20professional%20corporate%20feel%2C%20no%20text%2C%20subtle%20glow%20effects%2C%20high%20quality%20render&width=1600&height=900&seq=hero-bg-91n&orientation=landscape"
              alt="91.n 官方导航入口"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/40"></div>
          <div className="relative z-10 w-full text-center px-4">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
                值得信赖的<span className="text-accent-400">网络导航入口</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                快速、安全、便捷的访问体验。汇聚核心资源，轻松上手，畅享高效网络服务。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/all"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-base transition-all duration-200 whitespace-nowrap"
                >
                  开始导航
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/article/guide"
                  className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm rounded-full font-medium text-base transition-all duration-200 border border-white/20 whitespace-nowrap"
                >
                  使用指南
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Category Grid */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">分类导航</h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">八大分类板块，覆盖您上网所需的所有资源类型</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {categories.filter(c => c.slug !== 'all').map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-200 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-200">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm mb-1">{cat.name}</h3>
                  <p className="text-foreground-500 text-xs">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Resources */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-2">推荐资源</h2>
                <p className="text-foreground-600 text-base">精选优质网站，一键直达</p>
              </div>
              <Link
                to="/category/all"
                className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredResources.map((resource) => (
                <a
                  key={resource.id}
                  href={resource.url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-300 transition-all duration-200 flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-500 transition-colors duration-200">
                    <i className={`${resource.icon} text-lg text-primary-600 group-hover:text-background-50 transition-colors duration-200`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1 group-hover:text-primary-500 transition-colors">{resource.title}</h3>
                    <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2">{resource.description}</p>
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {resource.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-background-100 text-foreground-500 text-xs rounded-full">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-foreground-400 group-hover:text-primary-500 transition-colors">
                    <i className="ri-external-link-line text-sm"></i>
                  </div>
                </a>
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
              >
                查看全部资源 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">为什么选择 91.n</h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">六大核心优势，为您打造卓越的导航体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-primary-200 transition-all duration-200">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-base mb-2">{feature.title}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">用户心声</h2>
              <p className="text-foreground-600 text-base">听听用户怎么说 91.n</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item, index) => (
                <div key={index} className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-amber-400 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">&ldquo;{item.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-sm text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.author}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl md:text-5xl font-bold text-background-50 mb-1">200+</p>
                <p className="text-primary-100 text-sm">收录网站</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-background-50 mb-1">8</p>
                <p className="text-primary-100 text-sm">分类板块</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-background-50 mb-1">10万+</p>
                <p className="text-primary-100 text-sm">日访问量</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-background-50 mb-1">99.9%</p>
                <p className="text-primary-100 text-sm">服务可用率</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">常见问题</h2>
              <p className="text-foreground-600 text-base">快速了解 91.n 的使用方法与服务特色</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-background-200/70 rounded-xl overflow-hidden">
                  <button
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-background-100 transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-medium text-foreground-900 text-sm pr-4">{faq.question}</span>
                    <i className={`text-foreground-400 text-lg transition-transform duration-200 flex-shrink-0 ${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4">立即开始使用 91.n</h2>
            <p className="text-foreground-600 text-base mb-8">将 91.n 设为浏览器首页，享受便捷高效的网络导航体验</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-base transition-all duration-200 whitespace-nowrap"
              >
                开始探索
                <i className="ri-compass-3-line ml-2"></i>
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-background-300 text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 rounded-full font-medium text-base transition-all duration-200 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}